import type { dynamicObj } from 'src/pages/assets';
import { i18n } from 'src/boot/i18n';

export type ApiErrorMessage = string & {
  readonly detail?: string;
  readonly fullDetail?: string;
  readonly code?: string;
  readonly status?: number;
};

const REDACTED_KEYS = [
  'token',
  'password',
  'secret',
  'authorization',
  'cookie',
  'session',
  'key',
];

export abstract class ExceptionClass {
  public general_error_label = 'error_internal_occur';
  protected constructor() {
    // if needed
  }

  getErrorMessage(response: dynamicObj): ApiErrorMessage {
    const text = this.extractMessage(response);
    const { short: detail, full: fullDetail } = this.extractDetail(response);
    const code = this.extractCode(response);
    const status = this.extractStatus(response);

    if (!detail && !code && status === undefined) {
      return text as ApiErrorMessage;
    }

    const wrapper = new String(text) as unknown as Record<string, unknown>;
    if (detail) wrapper.detail = detail;
    if (fullDetail && fullDetail !== detail) wrapper.fullDetail = fullDetail;
    if (code) wrapper.code = code;
    if (status !== undefined) wrapper.status = status;
    return wrapper as unknown as ApiErrorMessage;
  }

  translate(translation_key: string) {
    let translation = '';
    try {
      translation = !translation_key ? '' : i18n.t(translation_key);
      if (translation == translation_key) {
        translation = i18n.t(translation_key);
      }
    } catch (err) {
      logError(err);
    }
    return translation;
  }

  private extractMessage(response: dynamicObj): string {
    let error_msg = '';
    try {
      let response_msg = '';
      let error_code_msg = '';
      let payload = response;

      // Drill through nested arrays — e.g. [['msg']] → 'msg', [{message}] → {message}
      while (Array.isArray(payload) && payload.length > 0) {
        payload = payload[0];
      }
      if (typeof payload === 'string') {
        return payload;
      }

      if (
        payload?.hasOwnProperty?.('data') &&
        (payload.data?.message ||
          payload.data?.error_code ||
          payload.data?.error_description)
      ) {
        payload = payload.data;
      }

      if (payload?.hasOwnProperty?.('message')) {
        response_msg = payload.message;
      } else if (payload?.hasOwnProperty?.('error_description')) {
        response_msg = payload.error_description;
      }

      if (payload?.hasOwnProperty?.('error_code')) {
        const error_code = this.translate(payload.error_code);
        error_code_msg = payload.error_code === error_code ? '' : error_code;
      }

      const general_error_translation = this.translate(
        this.general_error_label,
      );

      error_msg = error_code_msg
        ? error_code_msg
        : response_msg
          ? response_msg
          : general_error_translation;
    } catch (err) {
      logError(err);
      error_msg = this.translate(this.general_error_label);
    }
    return error_msg;
  }

  private extractDetail(error: dynamicObj): { short?: string; full?: string } {
    const headerLines: string[] = [];
    let payloadJson: string | undefined;
    try {
      const response = error?.response as dynamicObj | undefined;
      const responseData = response?.data as dynamicObj | undefined;
      const status = response?.status ?? error?.status;
      const config = (error?.config ?? response?.config) as dynamicObj | undefined;
      const isAxiosError = error?.isAxiosError === true;
      const messageSource = (responseData ?? (isAxiosError ? undefined : error?.data)) as
        | dynamicObj
        | undefined;
      const networkMessage = isAxiosError && !response ? (error?.message as string | undefined) : undefined;

      if (status) headerLines.push(`HTTP ${status}`);
      if (messageSource?.error_code) headerLines.push(`Code: ${messageSource.error_code}`);
      const msg = messageSource?.message ?? networkMessage;
      if (msg) headerLines.push(`Message: ${msg}`);
      if (config?.url) {
        const method = ((config.method as string) || 'GET').toUpperCase();
        headerLines.push(`Request: ${method} ${config.url}`);
      }

      if (responseData && typeof responseData === 'object') {
        const { error_code, message, ...rest } = responseData;
        const safeRest = Object.fromEntries(
          Object.entries(rest).filter(
            ([k]) => !REDACTED_KEYS.includes(k.toLowerCase()),
          ),
        );
        if (Object.keys(safeRest).length > 0) {
          payloadJson = JSON.stringify(safeRest, null, 2);
        }
      }
    } catch {
      return {};
    }

    const header = headerLines.join('\n');
    if (!header && !payloadJson) return {};

    const short = header || undefined;
    const full = payloadJson ? `${header}\n\nPayload:\n${payloadJson}`.trim() : header;
    return { short, full: full || undefined };
  }

  private extractCode(error: dynamicObj): string | undefined {
    try {
      const data =
        error?.response?.data ?? error?.data ?? error;
      const code = data?.error_code;
      return typeof code === 'string' ? code : undefined;
    } catch {
      return undefined;
    }
  }

  private extractStatus(error: dynamicObj): number | undefined {
    try {
      const status = error?.response?.status ?? error?.status;
      return typeof status === 'number' ? status : undefined;
    } catch {
      return undefined;
    }
  }
}
